import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import styled, { Color } from '../../styled'
import { Ripple } from '~/app/components/common/Animations'
import usePreventDoubleEvent from '~/app/utils/usePreventDoubleEvent'
import Impression from '~/app/components/common/Impression'

/**
 * Button component
 * author: soojin.oh
 */

// type definitions
export enum BUTTON_TYPE {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  BASIC = 'BASIC',
  BLUE = 'BLUE',
}
export enum BUTTON_SIZE {
  XS = 'XS',
  S = 'S',
  M = 'M',
  L = 'L',
  XL = 'XL',
}

type Props = {
  btnType?: React.ComponentProps<'button'>['type']
  type: BUTTON_TYPE
  size: BUTTON_SIZE
  title?: string
  isDisabled?: boolean
  id?: string
  onClick?: (id: string | null, event: React.MouseEvent) => void
  onDisabledClick?: (id: string | null, event: React.MouseEvent) => void
  isFullSize?: boolean
  isRipple?: boolean
  preventDoubleTab?: {
    enabled: boolean
    delay: number
  }
  dataClipboardText?: string
  className?: string
  isDisabledEventDelegation?: boolean
  onImpressed?: () => void
  testId?: string
  iconElement?: React.ReactElement
} & Pick<React.HTMLProps<HTMLButtonElement>, 'style'>

type ColorSet = {
  enabled: Color
  pressed: Color
  disabled: Color
  hover: Color
}
type theme = {
  button: ColorSet
  text: ColorSet
  line?: ColorSet
}
type padding = [UP: number, RIGHT: number, DOWN: number, LEFT?: number]
type size = {
  padding: padding
  iconOnlyPadding: padding
  height: number
  icon: [SUB: number, MAIN: number] // 택스트와 함께 사용될때는 sub 사이즈를 사용하고 아이콘 단독으로 사용될때는 main 사이즈를 사용한다.
  font: number
  isBold: boolean
}

// CONSTANT
const BUTTON_THEME_COLOR: { [P in keyof typeof BUTTON_TYPE]: theme } = {
  PRIMARY: {
    button: {
      enabled: 'blue600',
      pressed: 'Ic_blue_hover',
      disabled: 'gray400',
      hover: 'Ic_blue_hover',
    },
    text: {
      enabled: 'white',
      pressed: 'white',
      disabled: 'white',
      hover: 'white',
    },
    line: {
      enabled: 'blue600',
      pressed: 'Ic_blue_hover',
      disabled: 'gray400',
      hover: 'Ic_blue_hover',
    },
  },
  SECONDARY: {
    button: {
      enabled: 'white',
      pressed: 'gray100',
      disabled: 'gray200',
      hover: 'gray50',
    },
    text: {
      enabled: 'blue600',
      pressed: 'blue600',
      disabled: 'gray500',
      hover: 'blue600',
    },
    line: {
      enabled: 'blue600',
      pressed: 'blue600',
      disabled: 'gray400',
      hover: 'blue600',
    },
  },
  BASIC: {
    button: {
      enabled: 'white',
      pressed: 'gray100',
      disabled: 'gray200',
      hover: 'gray50',
    },
    text: {
      enabled: 'gray900',
      pressed: 'gray900',
      disabled: 'gray500',
      hover: 'gray900',
    },
    line: {
      enabled: 'gray400',
      pressed: 'gray400',
      disabled: 'gray400',
      hover: 'gray400',
    },
  },
  BLUE: {
    button: {
      enabled: 'white',
      pressed: 'gray100',
      disabled: 'gray200',
      hover: 'gray50',
    },
    text: {
      enabled: 'gray900',
      pressed: 'gray900',
      disabled: 'gray500',
      hover: 'gray900',
    },
    line: {
      enabled: 'gray900',
      pressed: 'gray900',
      disabled: 'gray400',
      hover: 'gray900',
    },
  },
}

const BUTTON_THEME_SIZE: { [P in keyof typeof BUTTON_SIZE]: size } = {
  XS: {
    padding: [7, 8, 6],
    iconOnlyPadding: [6, 6, 6],
    height: 28,
    icon: [14, 16],
    font: 12,
    isBold: false,
  },
  S: {
    padding: [9, 16, 8],
    iconOnlyPadding: [6, 6, 6],
    height: 32,
    icon: [14, 20],
    font: 12,
    isBold: false,
  },
  M: {
    padding: [10, 16, 9],
    iconOnlyPadding: [8, 8, 8],
    height: 36,
    icon: [16, 20],
    font: 14,
    isBold: false,
  },
  L: {
    padding: [12, 16, 11],
    iconOnlyPadding: [8, 8, 8],
    height: 40,
    icon: [16, 24],
    font: 14,
    isBold: true,
  },
  XL: {
    padding: [13, 16, 12],
    iconOnlyPadding: [10, 10, 10],
    height: 44,
    icon: [18, 24],
    font: 16,
    isBold: true,
  },
}

const arrayToPadding = ({ padding }) =>
  padding.reduce((prev = '', item) => prev + (item ? `${item / 10}rem ` : '0 '), '')

// styled components
const Container = styled.button<
  {
    padding: padding
    height: number
    font: number
    isBold: boolean
    button: ColorSet
    line?: ColorSet
    text: ColorSet
    isFullSize: boolean
  } & Pick<React.ComponentProps<'button'>, 'type'>
>`
  &[type='button'],
  &[type='reset'],
  &[type='submit'] {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 6.4rem;
    min-height: 2.8rem;
    padding: ${arrayToPadding};
    height: ${({ height }) => height / 10}rem;
    border-radius: 0.4rem;
    background: ${({ button, theme }) => theme.color[button.enabled]};
    ${({ line, theme }) => (line ? `border: solid .1rem ${theme.color[line.enabled]}` : '')};
    color: ${({ text, theme }) => theme.color[text.enabled]};
    font-size: ${({ font }) => font / 10}rem;
    font-weight: ${({ isBold }) => (isBold ? 'bold' : 'normal')};
    cursor: pointer;
    ${({ isFullSize }) => (isFullSize ? `width: 100%; height: 100%;` : '')};
    overflow: hidden;
    white-space: nowrap;
    user-select: none;

    @media (min-width: ${({ theme }) => theme.unit.PC_WIDTH}px) {
      :hover {
        ${({ line, theme }) => (line ? `border: solid .1rem ${theme.color[line.hover]}` : '')};
        background: ${({ button, theme }) => theme.color[button.hover]};
        color: ${({ text, theme }) => theme.color[text.hover]};
      }
    }
    &.hover {
      ${({ line, theme }) => (line ? `border: solid .1rem ${theme.color[line.hover]}` : '')};
      background: ${({ button, theme }) => theme.color[button.hover]};
      color: ${({ text, theme }) => theme.color[text.hover]};
    }

    :active {
      ${({ line, theme }) => (line ? `border: solid .1rem ${theme.color[line.pressed]}` : '')};
      background: ${({ button, theme }) => theme.color[button.pressed]};
      color: ${({ text, theme }) => theme.color[text.pressed]};
    }

    &.disabled {
      ${({ line, theme }) => (line ? `border: solid .1rem ${theme.color[line.disabled]}` : '')};
      background: ${({ button, theme }) => theme.color[button.disabled]};
      color: ${({ text, theme }) => theme.color[text.disabled]};
    }
    &.icon-only {
      min-width: 0;
    }

    & span.ripple {
      position: absolute;
      border-radius: 50%;
      transform: scale(0);
      animation: ${Ripple} 1000ms ease;
      opacity: 0.5;
      background: ${({ text, theme }) => theme.color[text.enabled]};
    }

    &.primary span.ripple {
      background: ${({ text, theme }) => theme.color[text.enabled]};
    }

    &.disabled {
      cursor: not-allowed;
    }
  }
`

const IconWrapper = styled.span`
  display: flex;
`

const SubIconWrapper = styled.span`
  .sub-icon {
    margin-right: 0.2rem;
    margin-bottom: -0.2rem;
  }
  svg {
    display: inline;
    vertical-align: baseline;
  }
`

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`

// Button component
const Button = function ({
  btnType = 'button',
  type,
  size,
  title,
  isDisabled = false,
  id,
  onClick,
  onDisabledClick,
  isFullSize = false,
  isRipple = false,
  preventDoubleTab = {
    enabled: true,
    delay: 1000,
  },
  dataClipboardText,
  className,
  style,
  isDisabledEventDelegation = false,
  onImpressed,
  testId,
  iconElement,
  children,
}: PropsWithChildren<Props>) {
  const { button, line, text } = useMemo<theme>(() => BUTTON_THEME_COLOR[type], [type])
  const { padding, height, font, isBold, icon, iconOnlyPadding } = useMemo<size>(
    () => BUTTON_THEME_SIZE[size],
    [size]
  )
  const isIconOnly = !title && !!iconElement
  const [touch, setTouch] = useState(false)

  const classes = useMemo(
    () =>
      [
        isDisabled ? 'disabled' : '',
        isIconOnly ? 'icon-only' : '',
        type === BUTTON_TYPE.PRIMARY ? 'primary' : '',
        touch ? 'hover' : '',
        className,
      ]
        .filter(item => !!item)
        .join(' '),
    [isDisabled, isIconOnly, type, className, touch]
  )
  const createRipple = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const button = event.currentTarget
    const { clientWidth, clientHeight, offsetLeft, offsetTop } = button
    const circle = document.createElement('span')
    const diameter = Math.max(clientWidth, clientHeight)
    const radius = diameter / 2
    circle.style.width = circle.style.height = `${diameter / 10}rem`
    circle.style.left = `${(event.clientX - (offsetLeft + radius)) / 10}rem`
    circle.style.top = `${(event.clientY - (offsetTop + radius)) / 10}rem`
    circle.classList.add('ripple')
    const ripple = button.getElementsByClassName('ripple')[0]
    if (ripple) {
      ripple.remove()
    }
    button.appendChild(circle)
  }, [])

  const onClickCallback = useCallback(
    event => {
      if (isDisabledEventDelegation) event.stopPropagation()
      isRipple && createRipple(event)
      !isDisabled && onClick?.(id ?? null, event)
      isDisabled && onDisabledClick?.(id ?? null, event)
    },
    [isRipple, isDisabled, onClick, id, onDisabledClick]
  )
  const delay = preventDoubleTab?.enabled ? preventDoubleTab.delay : 0
  const [onButtonClick] = usePreventDoubleEvent(onClickCallback, delay)
  return (
    <Container
      disabled={isDisabled}
      type={btnType}
      padding={isIconOnly ? iconOnlyPadding : padding}
      height={height}
      font={font}
      isBold={isBold}
      button={button}
      line={line}
      text={text}
      className={classes}
      onClick={onButtonClick}
      isFullSize={isFullSize}
      style={style}
      onTouchStart={() => setTouch(true)}
      onTouchEnd={() => setTouch(false)}
      data-clipboard-text={dataClipboardText}
      data-testid={testId || 'button'}
    >
      {children && <Impression onImpressed={onImpressed}>{children}</Impression>}
      {!children && (
        <Impression onImpressed={onImpressed}>
          <FlexContainer>
            {!!title && !!iconElement && (
              <SubIconWrapper>
                {React.cloneElement(iconElement, { size: icon[0], className: 'sub-icon' })}
              </SubIconWrapper>
            )}
            {isIconOnly && (
              <IconWrapper>{React.cloneElement(iconElement, { size: icon[1] })}</IconWrapper>
            )}
            {title}
          </FlexContainer>
        </Impression>
      )}
    </Container>
  )
}

export default Button
